import { useState } from 'react';
import { NavBar } from '../components/NavBar';
import { AutocompleteInput } from '../components/AutocompleteInput';
import { PrimaryButton } from '../components/PrimaryButton';
import { useApp, type LocationInput } from '../state/store';
import { geocodeAddress, reverseGeocode } from '../lib/geocode';

export function Screen1Start() {
  const origin = useApp((s) => s.origin);
  const destination = useApp((s) => s.destination);
  const setOrigin = useApp((s) => s.setOrigin);
  const setDestination = useApp((s) => s.setDestination);
  const setScreen = useApp((s) => s.setScreen);

  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  // Knappen visas så fort båda fält har text — även när man autofyllt/skrivit
  // utan att peka på ett förslag. Oupplöst text geokodas vid Fortsätt.
  const canContinue = origin.label.trim() !== '' && destination.label.trim() !== '';

  // Löser upp ett fält till ett konkret ställe (place_id). Redan valda ställen
  // (eller "min position", som bär en koordinat i placeId) släpps igenom orörda.
  const resolveField = async (
    loc: LocationInput,
    fieldName: string,
  ): Promise<{ loc: LocationInput } | { error: string }> => {
    if (loc.placeId) return { loc };
    const text = loc.label.trim();
    if (!text) return { error: `Fyll i ${fieldName}.` };
    const r = await geocodeAddress(text);
    if (!r) return { error: `Vi hittade inte "${text}". Kontrollera adressen eller välj ur listan.` };
    return { loc: { label: r.label, placeId: r.placeId } };
  };

  const handleContinue = async (): Promise<void> => {
    if (resolving) return;
    setResolveError(null);
    if (origin.placeId && destination.placeId) {
      setScreen(2);
      return;
    }
    setResolving(true);
    try {
      const [o, d] = await Promise.all([
        resolveField(origin, 'start'),
        resolveField(destination, 'mål'),
      ]);
      if ('error' in o) {
        setResolveError(o.error);
        return;
      }
      if ('error' in d) {
        setResolveError(d.error);
        return;
      }
      // Sätt båda först när bägge lyckats, så vi aldrig går vidare halvupplöst.
      setOrigin(o.loc);
      setDestination(d.loc);
      setScreen(2);
    } catch {
      setResolveError('Något gick fel när vi slog upp adressen. Försök igen.');
    } finally {
      setResolving(false);
    }
  };

  const useMyLocation = (): void => {
    if (!('geolocation' in navigator)) {
      setGeoError('Din webbläsare stöder inte platsdata.');
      return;
    }
    setGeoLoading(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latlng = `${pos.coords.latitude.toFixed(6)},${pos.coords.longitude.toFixed(6)}`;
        setOrigin({ label: 'Min plats', placeId: latlng });
        setGeoLoading(false);
        // Slå upp adressen så användaren ser var hen står (faller tillbaka på "Min plats").
        void reverseGeocode(latlng)
          .then((r) => {
            // Skriv bara om positionen fortfarande är valt startfält — annars
            // klampar ett sent svar över något användaren hunnit välja själv.
            if (r && useApp.getState().origin.placeId === latlng) {
              setOrigin({ label: r.label, placeId: latlng });
            }
          })
          .catch(() => {});
      },
      (err) => {
        // iOS/Safari nekar geolocation blankt utan HTTPS — då hjälper det inte
        // att be användaren ändra webbläsarens platsinställning.
        const msg = !window.isSecureContext
          ? 'Platsdata kräver en säker anslutning (HTTPS).'
          : err.code === err.PERMISSION_DENIED
            ? 'Plats nekad. Tillåt platsdata i webbläsaren.'
            : 'Kunde inte hämta din plats.';
        setGeoError(msg);
        setGeoLoading(false);
      },
      { enableHighAccuracy: false, timeout: 8000 },
    );
  };

  return (
    <div className="screen screen--photo screen--start">
      <NavBar title="Bilmat" />
      <div className="content">
        <div className="intro-text">
          <p>
            Hitta perfekta restauranger längs din resrutt. Välj när du vill äta, så visar vi ställen
            som passar ditt tidsintervall.
          </p>
        </div>

        <AutocompleteInput
          label="Jag reser från"
          placeholder="Start — adress eller ort"
          variant="origin"
          value={origin.label}
          onChange={(label) => {
            setResolveError(null);
            setOrigin({ label, placeId: null });
          }}
          onSelect={(p) => setOrigin({ label: p.description, placeId: p.placeId })}
          onUseLocation={useMyLocation}
          locating={geoLoading}
        />
        {geoError && <div className="autocomplete-error">{geoError}</div>}

        <AutocompleteInput
          label="Jag reser till"
          placeholder="Mål — adress eller ort"
          variant="dest"
          value={destination.label}
          onChange={(label) => {
            setResolveError(null);
            setDestination({ label, placeId: null });
          }}
          onSelect={(p) => setDestination({ label: p.description, placeId: p.placeId })}
        />

        <div className={`cta-slot${canContinue ? ' is-visible' : ''}`}>
          <div className="cta-slot-inner">
            {resolveError && <div className="autocomplete-error cta-error">{resolveError}</div>}
            <PrimaryButton onClick={() => void handleContinue()} disabled={resolving}>
              {resolving ? 'Söker adress…' : 'Välj när du vill äta'}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}
